import type { AgentResultArtifact } from "ApiLayer/Chatbot/queryBuildingChatHistory";

const EQUIPMENT_SURVEY_SECTION_KEYS = new Set([
    "rooftopUnit",
    "airHandlingEquipment",
    "centralPlantCooler",
    "centralPlantHeater",
    "centralPlantHeatPump",
    "terminalCooler",
    "terminalHeater",
    "heatPump",
    "domesticHotWaterHeater",
    "otherEquipment",
]);

const BOOL_NAMED_FIELDS = new Set(["domesticHotWaterHeaterCentralDistribution", "escalatorVariableFrequencyDrive"]);

function normalizedValue(raw: unknown): string {
    if (raw == null) {
        return "";
    }
    return String(raw).trim();
}

function asBoolOrNone(raw: unknown): boolean | null {
    const value = normalizedValue(raw).toLowerCase();
    if (value === "true" || value === "yes") {
        return true;
    }
    if (value === "false" || value === "no") {
        return false;
    }
    return null;
}

function asNumberOrNone(raw: unknown): number | null {
    const value = normalizedValue(raw);
    if (!value || value.toLowerCase() === "undetermined") {
        return null;
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
        return null;
    }
    return Number.isInteger(numeric) ? Math.trunc(numeric) : numeric;
}

function asTextOrNone(raw: unknown): string | null {
    const value = normalizedValue(raw);
    if (!value) {
        return null;
    }
    const lowered = value.toLowerCase();
    if (lowered === "undetermined") {
        return null;
    }
    if (lowered === "true" || lowered === "false") {
        return null;
    }
    return lowered;
}

function coercePrefillValue(field: string, raw: unknown): boolean | number | string | null {
    if (field.endsWith("Exists") || BOOL_NAMED_FIELDS.has(field)) {
        return asBoolOrNone(raw);
    }
    const asNum = asNumberOrNone(raw);
    if (asNum !== null) {
        return asNum;
    }
    return asTextOrNone(raw);
}

function parseSurveySectionAndField(raw: string): [string, string] | null {
    const s = raw.trim();
    const dot = s.indexOf(".");
    if (dot < 0) {
        return null;
    }
    const section = s.slice(0, dot).trim();
    const field = s.slice(dot + 1).trim();
    if (!section || !field || !EQUIPMENT_SURVEY_SECTION_KEYS.has(section)) {
        return null;
    }
    return [section, field];
}

export function equipmentTopologyArtifactToSurveyPrefill(
    artifact: AgentResultArtifact
): Partial<EquipmentSurvey> | null {
    if (artifact.agent !== "equipment_topology") {
        return null;
    }
    const table = artifact.data.table;
    if (!Array.isArray(table) || table.length === 0) {
        return null;
    }

    const prefill: Record<string, Record<string, boolean | number | string | null>> = {};
    table.forEach((row) => {
        if (!row || typeof row !== "object") {
            return;
        }
        const r = row as Record<string, unknown>;
        const subRaw = r.subcategory_name ?? r.subcategory;
        const parsed = parseSurveySectionAndField(String(subRaw ?? ""));
        if (!parsed) {
            return;
        }
        const [section, field] = parsed;
        const converted = coercePrefillValue(field, r.value);
        if (!prefill[section]) {
            prefill[section] = {};
        }
        prefill[section][field] = converted;
    });

    if (Object.keys(prefill).length === 0) {
        return null;
    }
    return prefill as Partial<EquipmentSurvey>;
}
